import { useEffect, useMemo, useState } from 'react';

// Extracts the dirty-tracking pattern hand-rolled per-page elsewhere
// (FormStudent.jsx, BuilderHeader.jsx): formValues/initialValues snapshots,
// isDirty via JSON.stringify comparison, discard-to-initial, save-with-
// in-flight-guard. Reused identically 6 times across the Kursy module
// (Course + the 5 hierarchy levels all share the {name, description} shape),
// so - unlike those two call sites - it's worth lifting into one hook here.
//
// flatten(source) must return a plain {field: value} snapshot; onSave(values)
// must return a promise resolving to the freshly-saved source object (or
// anything flatten() can re-derive initialValues from).
export default function useDirtyForm(source, flatten, onSave) {
    const [formValues, setFormValues] = useState(null);
    const [initialValues, setInitialValues] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!source) {
            setFormValues(null);
            setInitialValues(null);
            return;
        }
        const flat = flatten(source);
        setFormValues(flat);
        setInitialValues(flat);
        // flatten intentionally excluded - callers pass a stable function
        // reference (module-level or useCallback), re-running only when the
        // underlying source (or which node is selected) actually changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    const isDirty = useMemo(() => {
        if (!formValues || !initialValues) return false;
        return JSON.stringify(formValues) !== JSON.stringify(initialValues);
    }, [formValues, initialValues]);

    const handleFieldChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleDiscard = () => {
        setFormValues(initialValues);
    };

    const handleSave = () => {
        if (!isDirty || saving || !formValues) return Promise.resolve();
        setSaving(true);
        return onSave(formValues)
            .then((saved) => {
                const flat = saved ? flatten(saved) : formValues;
                setFormValues(flat);
                setInitialValues(flat);
            })
            .finally(() => setSaving(false));
    };

    return { formValues, isDirty, saving, handleFieldChange, handleDiscard, handleSave };
}
